import { useState, useMemo, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/UseAuth';
// --- 1. IMPORTAR NUEVOS SERVICIOS ---
import { createPost } from '../services/posts.service';
import { getAvailableEffects } from '../services/packs.service';
import {
  getEffectPreview,
  requestPresignedUrl,
  uploadFileDirectly,
  confirmUpload,
} from '../services/media.service';
// -------------------------------------
import { generateVideoThumbnailFromFile } from '../utils/videoUtils';
import { useDebouncedCallback } from 'use-debounce';
import toast from 'react-hot-toast';
import { useExchangeRates } from './useExchangeRates';

const getDefaultIntensity = (effectType, effects = {}) =>
  effects[effectType]?.defaultValue ?? 50;

const BASE_PRICE_USD = 5;

// --- 2. NUEVO ESTADO PARA EL PROCESO DE SUBIDA ---
const UPLOAD_STATE = {
  IDLE: 'idle',
  CREATING_DRAFT: 'creating_draft',
  REQUESTING_URL: 'requesting_url',
  UPLOADING: 'uploading',
  CONFIRMING: 'confirming',
  SUCCESS: 'success',
};
// ------------------------------------------------

export const useCreatePost = (onSuccess) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: rates, isLoading: isLoadingRates } = useExchangeRates('USD');

  const [postData, setPostData] = useState({
    title: '',
    content: '',
    isPublic: true,
    isPremium: true,
    price: { amount: BASE_PRICE_USD, currency: user?.currency || 'USD' },
    commentsEnabled: true,
  });

  useEffect(() => {
    if (!isLoadingRates && rates && user?.currency) {
      const userCurrency = user.currency;
      if (userCurrency === 'USD') {
        setPostData((prev) => ({
          ...prev,
          price: { amount: BASE_PRICE_USD, currency: 'USD' },
        }));
        return;
      }
      const conversionRate = rates[userCurrency];
      if (conversionRate) {
        const convertedAmount = Math.round(BASE_PRICE_USD * conversionRate);
        setPostData((prev) => ({
          ...prev,
          price: { amount: convertedAmount, currency: userCurrency },
        }));
      }
    }
  }, [isLoadingRates, rates, user?.currency]);

  const [mainFile, setMainFile] = useState(null);
  const [mainThumbnailFile, setMainThumbnailFile] = useState(null);
  const [mainFileEffect, setMainFileEffect] = useState(null);

  const [coverFile, setCoverFile] = useState(null);
  const [coverFileEffect, setCoverFileEffect] = useState(null);

  const [error, setError] = useState(null);

  // --- 3. GESTIÓN DE ESTADO DE SUBIDA REFACTORIZADA ---
  const [uploadStatus, setUploadStatus] = useState(UPLOAD_STATE.IDLE);
  const [uploadProgress, setUploadProgress] = useState(null); // Corregido a null
  const isPending =
    uploadStatus !== UPLOAD_STATE.IDLE && uploadStatus !== UPLOAD_STATE.SUCCESS;
  // ----------------------------------------------------

  const [isEffectModalOpen, setIsEffectModalOpen] = useState(false);
  const [effectModalContext, setEffectModalContext] = useState(null);
  const [modalPreviewState, setModalPreviewState] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [temporaryCoverPreviewUrl, setTemporaryCoverPreviewUrl] =
    useState(null);

  const { data: effectsData, isLoading: loadingEffects } = useQuery({
    queryKey: ['effects'],
    queryFn: getAvailableEffects,
    staleTime: Infinity,
  });

  const defaultEffectType = useMemo(
    () => effectsData?.defaultEffect || 'blur',
    [effectsData]
  );

  const { mutate: previewMutation } = useMutation({
    mutationFn: (vars) =>
      getEffectPreview(
        vars.sourceFile,
        null,
        vars.effectType,
        vars.effectIntensity
      ),
    onSuccess: (blobUrl) => {
      setModalPreviewState((prev) => ({ ...prev, url: blobUrl }));
    },
    onError: () => toast.error('No se pudo generar la previsualización.'),
    onSettled: () => setIsPreviewing(false),
  });

  const { mutate } = useMutation({
    mutationFn: createPost,
    onSuccess: async (newPost) => {
      try {
        const postId = newPost._id;

        // --- SUBIDA DEL ARCHIVO PRINCIPAL ---
        setUploadStatus(UPLOAD_STATE.REQUESTING_URL);
        const { presignedUrl: mainUrl, objectKey: mainKey } =
          await requestPresignedUrl(mainFile, 'posts', postId);

        setUploadStatus(UPLOAD_STATE.UPLOADING);
        await uploadFileDirectly(mainUrl, mainFile, setUploadProgress);

        setUploadStatus(UPLOAD_STATE.CONFIRMING);
        // --- INICIO DE LA SOLUCIÓN (RACE CONDITION) ---
        // Si NO hay portada, esta es la última llamada, encolar worker.
        await confirmUpload(
          mainKey,
          'posts',
          postId,
          {}, // Sin metadata extra
          !coverFile // shouldQueueWorker: true si !coverFile
        );
        // --- FIN DE LA SOLUCIÓN ---

        // --- SUBIDA DE LA PORTADA (OPCIONAL) ---
        if (coverFile) {
          setUploadProgress(0);
          setUploadStatus(UPLOAD_STATE.REQUESTING_URL);
          const { presignedUrl: coverUrl, objectKey: coverKey } =
            await requestPresignedUrl(coverFile, 'post-cover', postId);

          setUploadStatus(UPLOAD_STATE.UPLOADING);
          await uploadFileDirectly(coverUrl, coverFile, setUploadProgress);

          setUploadStatus(UPLOAD_STATE.CONFIRMING);
          // --- INICIO DE LA SOLUCIÓN (RACE CONDITION) ---
          // Si SÍ hay portada, esta ES la última llamada, encolar worker.
          await confirmUpload(
            coverKey,
            'post-cover',
            postId,
            {}, // Sin metadata extra
            true // shouldQueueWorker: true siempre
          );
          // --- FIN DE LA SOLUCIÓN ---
        }

        setUploadStatus(UPLOAD_STATE.SUCCESS);
        toast.success('Post creado con éxito. Se está procesando.');
        queryClient.invalidateQueries({
          queryKey: ['userPosts', user?.username],
        });
        if (onSuccess) onSuccess(newPost);
      } catch (uploadError) {
        setError(uploadError.message || 'Error durante la subida del archivo.');
        setUploadStatus(UPLOAD_STATE.IDLE);
        setUploadProgress(null);
      }
    },
    onError: (err) => {
      setError(err.response?.data?.message || 'Error al crear el post.');
      setUploadStatus(UPLOAD_STATE.IDLE);
      setUploadProgress(null);
    },
  });

  // (Resto de funciones debouncedRequestPreview, handleFileSelect, etc. sin cambios)
  const debouncedRequestPreview = useDebouncedCallback(
    (file, effect, intensity) => {
      if (!file || !effect || effect === 'none') {
        setModalPreviewState((prev) => ({ ...prev, url: null }));
        setIsPreviewing(false);
        return;
      }
      setIsPreviewing(true);
      previewMutation({
        sourceFile: file,
        effectType: effect,
        effectIntensity: intensity,
      });
    },
    300
  );

  const handleFileSelect = async (file) => {
    if (file) {
      setMainFile(file);
      if (file.type.startsWith('video/')) {
        const thumb = await generateVideoThumbnailFromFile(file);
        setMainThumbnailFile(thumb);
      } else {
        setMainThumbnailFile(null);
      }
      if (postData.isPremium && !mainFileEffect) {
        setMainFileEffect({
          type: defaultEffectType,
          intensity: getDefaultIntensity(
            defaultEffectType,
            effectsData?.effects
          ),
        });
      }
    } else {
      setMainFile(null);
      setMainThumbnailFile(null);
      setMainFileEffect(null);
    }
  };

  const handleCoverFileChange = (file) => {
    setTemporaryCoverPreviewUrl(null);
    if (file) {
      setCoverFile(file);
      if (postData.isPremium && !coverFileEffect) {
        setCoverFileEffect({
          type: defaultEffectType,
          intensity: getDefaultIntensity(
            defaultEffectType,
            effectsData?.effects
          ),
        });
      }
    } else {
      setCoverFile(null);
      setCoverFileEffect(null);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPostData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleOpenEffectModal = (context) => {
    setEffectModalContext(context);
    const effectConfig = context === 'main' ? mainFileEffect : coverFileEffect;
    const intensity =
      effectConfig?.intensity ??
      getDefaultIntensity(effectConfig?.type, effectsData?.effects);
    setModalPreviewState({
      effectType: effectConfig?.type || defaultEffectType,
      intensity,
      url: null,
    });
    setIsEffectModalOpen(true);
    const fileForPreview =
      context === 'main' ? mainThumbnailFile || mainFile : coverFile;
    debouncedRequestPreview(fileForPreview, effectConfig?.type, intensity);
  };
  const handleModalEffectSelect = (effectType) => {
    setModalPreviewState((prev) => {
      const newIntensity = getDefaultIntensity(
        effectType,
        effectsData?.effects
      );
      const file =
        effectModalContext === 'main'
          ? mainThumbnailFile || mainFile
          : coverFile;
      debouncedRequestPreview(file, effectType, newIntensity);
      return { ...prev, url: null, effectType, intensity: newIntensity };
    });
  };
  const handleModalIntensityChange = (intensity) => {
    setModalPreviewState((prev) => {
      const file =
        effectModalContext === 'main'
          ? mainThumbnailFile || mainFile
          : coverFile;
      debouncedRequestPreview(file, prev.effectType, intensity);
      return { ...prev, intensity };
    });
  };
  const handleConfirmEffect = () => {
    const { effectType, intensity, url } = modalPreviewState;
    if (effectModalContext === 'main') {
      setMainFileEffect({ type: effectType, intensity });
    } else {
      setCoverFileEffect({ type: effectType, intensity });
      setTemporaryCoverPreviewUrl(url);
    }
    setIsEffectModalOpen(false);
    toast.success('Efecto preparado');
  };
  const getDisplayUrlForModal = () => {
    if (modalPreviewState?.url) return modalPreviewState.url;
    const file =
      effectModalContext === 'main' ? mainThumbnailFile || mainFile : coverFile;
    return file ? URL.createObjectURL(file) : null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    if (!postData.title || !mainFile) {
      setError('El título y el archivo principal son obligatorios.');
      return;
    }

    setUploadStatus(UPLOAD_STATE.CREATING_DRAFT);
    setUploadProgress(0);

    const draftData = {
      ...postData,
      mediaType: mainFile.type.startsWith('video/') ? 'video' : 'image',
      hasCustomCover: !!coverFile,
      effectType: mainFileEffect?.type,
      effectIntensity: mainFileEffect?.intensity,
      customCoverEffectType: coverFileEffect?.type,
      customCoverEffectIntensity: coverFileEffect?.intensity,
    };

    mutate(draftData);
  };

  return {
    postData,
    setPostData,
    mainFile,
    mainThumbnailFile,
    mainFileEffect,
    coverFile,
    coverFileEffect,
    handleFileSelect,
    handleCoverFileChange,
    handleChange,
    handleSubmit,
    effectsData,
    loadingEffects,
    isEffectModalOpen,
    setIsEffectModalOpen,
    handleOpenEffectModal,
    handleModalEffectSelect,
    handleModalIntensityChange,
    handleConfirmEffect,
    getDisplayUrlForModal,
    modalPreviewState,
    isPreviewing,
    isPending,
    uploadProgress,
    uploadStatus,
    UPLOAD_STATE,
    error,
    temporaryCoverPreviewUrl,
  };
};
